// The workspace's runners, the machines that run its agents, and adding one.
// The list follows the feed live (Poulpe-367): each runner, online or not and
// since when, its agents, the projects it serves and the jobs it owes, as
// the snapshot and the feed's `runner` and `job` changes leave them. A
// person adds a runner with a one-time code they
// make here, confirming it with their passkey: on the runner's machine,
// `dev/serve runner` asks for it. The code is shown once: the server keeps
// only its hash, and this page keeps it in its own state alone, which it
// forgets when it is left. It is never logged, nor put in the address.
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Runner, RunnerCode, RunnerJob } from '../api/types'
import { titleOf } from '../code/format'
import { useMounted } from '../components/layout'
import { exactTime, clockTime as dayClockTime } from '../components/Time'
import { confirmationProblemText } from '../people/confirmation'
import { sessionPath } from '../projects/routing'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'

/** A moment as a clock shows it, in the browser's own time zone. */
const CLOCK = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })

/** The clock time of `iso`, or `iso` itself when it is not a time. */
function clockTime(iso: string): string {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? iso : CLOCK.format(t)
}

/** What a job's work asks, by the protocol's kind (runners/jobs.rs there); another kind reads as it is named. */
const JOB_KINDS: Record<string, string> = {
  start_session: 'Session start',
  resume_session: 'Session resume',
  prompt_session: 'Prompt',
  set_session_settings: 'Session settings',
  interrupt_session: 'Interrupt',
  stop_subagent: 'Subagent stop',
  refresh_changes: 'Changes refresh',
  answer_question: 'Answer',
  stop_session: 'Session stop',
  handoff_session: 'Handoff',
  return_session: 'Return',
  run_check: 'Check',
  check_worktree: 'Check by hand',
  push_head: 'Push',
  build_candidate: 'Candidate',
  diff_proposal: 'Proposal diff',
}

/** A job's kind as the page names it. */
function jobKindText(kind: string): string {
  const named = Object.hasOwn(JOB_KINDS, kind) ? JOB_KINDS[kind] : undefined
  const words = kind.replaceAll('_', ' ')
  return named ?? words.charAt(0).toUpperCase() + words.slice(1)
}

export function RunnersPage() {
  const store = useStore()
  const { offline, status, runners: live, jobs } = useBoardState()
  const runners = [...live.values()]
  const mounted = useMounted()
  const [made, setMade] = useState<RunnerCode | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const make = async () => {
    if (busy || store.getState().offline) return
    setBusy(true)
    setProblem(null)
    const outcome = await store.makeRunnerCode()
    if (!mounted.current) return
    if (outcome.ok) setMade(outcome.made)
    else setProblem(confirmationProblemText(outcome, 'Could not make the code'))
    setBusy(false)
  }

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      if (mounted.current) setCopied(true)
    } catch {
      if (mounted.current) setProblem('Could not copy the code: select it and copy it yourself.')
    }
  }

  return (
    <section className="settings-section runners-page">
      <header className="runners-head">
        {!made && (
          <button type="button" className="button button-primary button-small" disabled={busy || offline} {...needsServer(offline)} onClick={() => void make()}>
            Add a runner
          </button>
        )}
      </header>
      {made && (
        <div className="runner-code-panel form-field">
          <label htmlFor="runner-code">Runner code</label>
          <div className="runner-code-row">
            <input id="runner-code" className="input runner-code" value={made.code} readOnly spellCheck={false} onFocus={(e) => e.currentTarget.select()} aria-describedby="runner-code-hint" />
            <button type="button" className="button button-primary" onClick={() => void copy(made.code)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <span id="runner-code-hint" className="muted form-field-hint">
            On the runner’s machine, <code>dev/serve runner</code> asks for it. Copy it now: it is shown only this once, and expires at{' '}
            <time dateTime={made.expires_at}>{clockTime(made.expires_at)}</time>.
          </span>
        </div>
      )}
      {problem && (
        <p className="form-problem" role="alert">
          {problem}
        </p>
      )}
      {status === 'loading' && runners.length === 0 ? (
        <p className="muted" role="status">
          Loading the runners…
        </p>
      ) : runners.length === 0 ? (
        <p className="muted">No runner is registered in this workspace yet.</p>
      ) : (
        <ul className="runner-list" aria-label="Runners">
          {runners.map((runner) => (
            <RunnerItem key={runner.id} runner={runner} jobs={[...jobs.values()].filter((job) => job.runner_id === runner.id)} />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * One runner: its name, its machine when that says more, whether it is online or revoked and since when, the agents it
 * offers, the projects it serves, and the jobs it owes, oldest first.
 */
function RunnerItem({ runner, jobs }: { runner: Runner; jobs: RunnerJob[] }) {
  const nameId = useId()
  const { projects } = useBoardState()
  const served = runner.projects.flatMap((id) => projects.get(id)?.name ?? [])
  return (
    <li className={runner.revoked ? 'runner-item is-revoked' : 'runner-item'} aria-labelledby={nameId}>
      <div className="runner-line">
        <h2 id={nameId} className="runner-name">
          {runner.name}
        </h2>
        {/* Its first hello names the runner after its machine: the machine is shown when it says more. */}
        {runner.machine && runner.machine !== runner.name && <span className="muted">{runner.machine}</span>}
        <span className={runner.connected ? 'tag tag-ok' : 'tag'}>{runner.connected ? 'Online' : 'Offline'}</span>
        {runner.since && (
          <span className="muted">
            since{' '}
            <time dateTime={runner.since} title={exactTime(runner.since)}>
              {dayClockTime(runner.since, Date.now())}
            </time>
          </span>
        )}
        {runner.revoked && <span className="tag tag-warn">Revoked</span>}
      </div>
      {runner.agents.length === 0 ? (
        <p className="runner-agents muted">This runner offers no agents.</p>
      ) : (
        <ul className="runner-agents" aria-label="Agents">
          {runner.agents.map((agent) => (
            <li key={agent.agent}>
              {agent.agent} <span className={agent.signed_in ? 'tag tag-ok' : 'tag'}>{agent.signed_in === null ? 'Sign-in unknown' : agent.signed_in ? 'Signed in' : 'Not signed in'}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="runner-projects muted">{served.length === 0 ? 'It serves no project of this workspace.' : `Serves ${served.join(', ')}.`}</p>
      {jobs.length > 0 && (
        <ul className="runner-jobs" aria-label="Open jobs">
          {jobs.map((job) => (
            <JobItem key={job.id} job={job} />
          ))}
        </ul>
      )}
    </li>
  )
}

/** One job a runner owes: what it asks, for which session or project, and whether the runner has taken it. */
function JobItem({ job }: { job: RunnerJob }) {
  const { sessions, issues, projects } = useBoardState()
  const project = projects.get(job.project_id)
  const session = job.session_id ? sessions.get(job.session_id) : undefined
  const taken = job.state === 'accepted' || job.state === 'started'
  return (
    <li>
      {jobKindText(job.kind)}{' '}
      {session && project ? (
        <Link to={sessionPath(project.key, session.id)}>{titleOf(session, session.issue_id ? issues.get(session.issue_id) : undefined, undefined) || 'Untitled session'}</Link>
      ) : (
        project && <span className="muted">{project.name}</span>
      )}{' '}
      <span className={taken ? 'tag tag-ok' : 'tag'}>{taken ? 'Running' : 'Waiting'}</span>
    </li>
  )
}
